<script>
  export let options;
  export let value;
  export let selected = "_default";
  export let multipleSelection = false;
  export let fit_content = false;
  export let idBasedValue = false;
  export let allSelected = null;
  export let enabled = true;
  export let floatingStrategy = "absolute";
  export let showError = false;

  import { createEventDispatcher, onMount, tick } from 'svelte';
  import { createPopperActions } from 'svelte-popperjs';
  import { _ } from '../localization';

  const dispatch = createEventDispatcher();

  function randomStr(len, arr="abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890") {
      var ans = '';
      for (var i = len; i > 0; i--) {
          ans +=
            arr[Math.floor(Math.random() * arr.length)];
      }
      return ans;
  }

  let generatedID = "polo-"+randomStr(6);

  $: {
    value;
    matchValueAndSelected();
  };

  $: {
    options;
    sortOptions();
  };


  let viewMounted = false;
  onMount(() => {
    viewMounted = true;
    resizeFitContent();
  });

  //Dropdown positioning for scrolling parent
  let [popperRef, popperContent, popperInstance] = createPopperActions({
    placement: 'bottom-start',
    strategy: floatingStrategy
  });
  const popperOpts = {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          mainAxis: false, // true by default
          altAxis: false, // true by default
          offset: [10, 10]
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom-start']
        },
      }
    ]
  };

  let collator = new Intl.Collator(undefined, {numeric: true, sensitivity: 'base'});
  function sortOptions(){
    options = options.sort((a, b) => collator.compare(a.title, b.title));
  }

  function matchValueAndSelected(){
    options.forEach((option, i) => {
      if (!multipleSelection) {
        if (idBasedValue) {
          options[i].selected = (value==option.id);
        }
        else{
          options[i].selected = (value==i);
        }
      }
      else{
        if (idBasedValue) {
          options[i].selected = value?.includes(option.id);

          if (allSelected?.id) {
            if (JSON.stringify(value) == JSON.stringify([allSelected?.id])) {
              options[i].selected = true;
            }
          }

        }
        else{
          options[i].selected = value?.includes(i);
        }
      }
    });

    resizeFitContent();
  }

  let fit_content_width = 0;
  let selected_width = 0;
  function resizeFitContent(){
    if (!viewMounted) {
      return;
    }

    tick().then(() => {
      let maxWidthInOptions = 0;
      let optionDiv = document.getElementById(generatedID);
      let prevDisplay = optionDiv.style.display;
      optionDiv.style.display = "block";
      [...optionDiv.getElementsByClassName("option_title")].forEach(option => {
        maxWidthInOptions = Math.max(maxWidthInOptions, option.clientWidth+70);
      });
      optionDiv.style.display = prevDisplay;

      const selectedOptionWidth = document.getElementsByClassName("selected_title-"+generatedID)[0].clientWidth;

      fit_content_width = Math.max(selectedOptionWidth, maxWidthInOptions);

      popperInstance().update(); //Update popper positioning

      tick().then(() => {
        const selected_field = document.getElementById(generatedID+"-selected");
        if (selected_field) {
          selected_width = selected_field.clientWidth;
          tick().then(() => {
            popperInstance().update(); //Update popper positioning
          });
        }
      });
    });


  }

  let optionsVisible = false;
  function selectedOptionClicked(){
    if (!enabled) {
      return;
    }
    optionsVisible = !optionsVisible;
    if (optionsVisible) {
      dispatch("focus");
      tick().then(() => {
        scrollToSelected(false);
        popperInstance().update(); //Update popper positioning
      });
      setTimeout(() => {
        window.addEventListener('click', clickedOut);
        window.addEventListener('keydown', dropdownKeydown);
      }, 100);
    }
    else{
      dispatch("blur");
    }
  }

  function clickedOut(e){
    if (!document.getElementById(generatedID)?.contains(e.target)){
      removeEventListener('click', clickedOut);
      removeEventListener('keydown', dropdownKeydown);
      dispatch("blur");
      optionsVisible = false;
    }
  }

  function scrollToSelected(animated){
    let scrollingSection = document.getElementById(generatedID);
    let targetOption = scrollingSection.getElementsByClassName("option")[indexForValue(value)];
    if (targetOption) {
      const scrollPosition = scrollingSection.scrollTop;
      const containerHeight = scrollingSection.clientHeight;
      const optionHeight = targetOption?.clientHeight;
      const optionPosition = targetOption?.offsetTop;

      if (scrollPosition > optionPosition || scrollPosition + containerHeight < optionPosition + optionHeight) {
        scrollingSection.scrollTo({top: optionPosition - (containerHeight)/2, behavior: animated ? 'smooth' : 'auto'});
      }
    }
  }

  function optionSelected(selectedId, index){
    dispatch("select", {
      id: selectedId
    });
    if (!multipleSelection) {
      optionsVisible = false;
      removeEventListener('click', clickedOut);
      removeEventListener('keydown', dropdownKeydown);
      dispatch("blur");
      if (idBasedValue) {
        value = options[index].id;
      }
      else{
        value = index;
      }
    }
    else{
      options[index].selected = !options[index].selected;
      let newValue = [];
      options.forEach((option, i) => {
        if (option.selected) {
          if (idBasedValue) {
            newValue.push(option.id);
          }
          else{
            newValue.push(i);
          }
        }
      });

      if (newValue?.length == options?.length && allSelected?.id) {
        value = [allSelected?.id];
      }
      else{
        value = newValue;
      }

    }
  }

  function defaultSelected(options){
    let selectedArr = [];
    options.forEach(option => {
      if (option.selected) {
        selectedArr.push($_(option.title));
      }
    });

    if (selectedArr?.length == options?.length && allSelected?.placeholder) {
      return allSelected?.placeholder;
    }
    else{
      return selectedArr.join(", ");
    }
  }

  function optionForValue(val){
    return indexForValue(val) > -1 ? options[indexForValue(val)] : null;
  }

  function indexForValue(val){
    if (idBasedValue) {
      for (var i = 0; i < options?.length; i++) {
          if (options[i].id == val) {
            return i;
          }
      }
      return -1;
    }
    else{
      return val;
    }
  }

  let accumulatedString = "";
  let accumulatedStringTimeout = null;
  function dropdownKeydown(e){
    const isLetter = (e.key >= 'a' && e.key <= 'z');
    if(isLetter) {

      //Update accumulatedString
      accumulatedString += e.key;
      resetSuggestionTimer();

      //Scroll to nearest match
      scrollToNearestMatch(accumulatedString);
    }
    else if (e.key == "ArrowDown" || e.key == "ArrowUp") {
      const movement = e.key == "ArrowUp" ? -1 : 1;

      let scrollingSection = document.getElementById(generatedID);
      let optionsLen = scrollingSection.getElementsByClassName("option").length;

      if (suggestedSelectionIndex !== null) {
        if (suggestedSelectionIndex+movement >= optionsLen) { //Back to first
          suggestedSelectionIndex = 0;
        }
        else if (suggestedSelectionIndex+movement < 0) { //Go to last
          suggestedSelectionIndex = optionsLen-1;
        }
        else{ //Move along
          suggestedSelectionIndex = Math.max(0, Math.min(optionsLen, suggestedSelectionIndex+movement));
        }
      }
      else{
        const visibleIndexes = scrollVisibleChildren(generatedID, "option");
        suggestedSelectionIndex = visibleIndexes.length > 1 ? visibleIndexes[1] : null;
      }

      //Scroll to index
      if (suggestedSelectionIndex !== null) {
        scrollToIndex(suggestedSelectionIndex);
      }

      //Reset timer
      resetSuggestionTimer();
    }
    else if (e.key == "Enter" && suggestedSelectionIndex !== null) {
      //Select the suggestion
      optionSelected(suggestedSelectionId, suggestedSelectionIndex)
    }
  }

  //Return indexes of visible children in scrollable div
  function scrollVisibleChildren(scrollId, childrenClass){
    let scrollingSection = document.getElementById(scrollId);
    let options = scrollingSection.getElementsByClassName(childrenClass);

    const scrollY = scrollingSection.scrollTop;
    const scrollContainerHeight = scrollingSection.clientHeight;

    let indexes = [];
    if (options?.length) {
      [...options].forEach((optionNode, i) => {
        const optionHeight = optionNode.clientHeight;
        const optionY = optionNode.offsetTop;

        if (optionY+optionHeight > scrollY && optionY < scrollY+scrollContainerHeight) {
          indexes.push(i);
        }
      });
    }
    return indexes;
  }

  function resetSuggestionTimer(){
    if (accumulatedStringTimeout) {
      clearTimeout(accumulatedStringTimeout);
    }
    accumulatedStringTimeout = setTimeout(() => {
      accumulatedString = "";
    }, 2500); //2.5 seconds to clear search string
  }

  let suggestedSelectionIndex = null;
  let suggestedSelectionId = null;
  let suggestionTimeout = null;
  function scrollToNearestMatch(search){
    let bestMatchIndex = -1;
    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      if (option.title.toLowerCase().startsWith(search)) {
        bestMatchIndex = i;
        break;
      }
    }

    if (bestMatchIndex > -1) {
      scrollToIndex(bestMatchIndex);
    }
  }

  function scrollToIndex(index){
    let scrollingSection = document.getElementById(generatedID);
    let targetOption = scrollingSection.getElementsByClassName("option")[index];
    if (targetOption) {
      //Show suggestion
      suggestedSelectionIndex = index;
      suggestedSelectionId = options[index].id;

      //Clear previous timouts
      if (suggestionTimeout) {
        clearTimeout(suggestionTimeout);
      }

      //Hide suggestion after 4 seconds
      suggestionTimeout = setTimeout(() => {
        suggestedSelectionIndex = null;
        suggestedSelectionId = null;
      }, 4000); //suggestion expires in 4 seconds.

      //Scroll to option
      const scrollPosition = scrollingSection.scrollTop;
      const containerHeight = scrollingSection.clientHeight;
      const optionHeight = targetOption?.clientHeight;
      const optionPosition = targetOption?.offsetTop;

      if (scrollPosition > optionPosition || scrollPosition + containerHeight < optionPosition + optionHeight) {
        scrollingSection.scrollTo({top: optionPosition - (containerHeight)/2, behavior: 'smooth'});
      }
    }
  }

</script>

<style>
  .field{
    position: relative;
    height: 50px;
    width: 100%;
  }
  .field_selected_option{
    height: 28px;
    width: calc(100% - 42px);
    box-sizing: content-box;
    padding: 10px 20px;
    border-radius: 12px;
    border: 1px solid var(--light-gray);
    background-color: var(--white);
    color: #000000;
    font-family: "roc-grotesk", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 14px;
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
    cursor: pointer;
    transition: all 200ms step-end, border 0ms step-start, background 0ms step-start, color 0ms step-start;
  }
  .show_error .field_selected_option{
    border-color: var(--red-error);
  }
  .disabled .field_selected_option{
    cursor: default;
    color: var(--gray);
    border-color: var(--extra-light-gray);
    background-color: var(--extra-light-gray);
  }
  .options{
    width: 100%;
    box-sizing: content-box;
    border-radius: 12px;
    border-color: transparent;
    color: #000000;
    font-family: "roc-grotesk", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 14px;
    position: absolute;
    background-color: #FFFFFF;
    top: 43px;
    display: block;
    z-index: 4;
    max-height: 0;
    overflow: scroll;
    margin-top: 5px !important;
    transition: max-height 200ms ease-out, border 200ms step-end, box-shadow 200ms linear;
    -ms-overflow-style: none;  /* Internet Explorer 10+ */
    scrollbar-width: none;  /* Firefox */
  }
  .options::-webkit-scrollbar {
    display: none;  /* Safari and Chrome */
  }
  .option_subtitle{
    font-size: 10px;
    line-height: 11px;
  }
  .optionsVisible .options{
    max-height: 209px; /* Odd number for visual cue of scroll */
    border: 1px solid var(--light-gray);
    -webkit-box-shadow: 0px 3px 6px 0px rgba(0,0,0,0.16);
    box-shadow: 0px 3px 6px 0px rgba(0,0,0,0.16);
    transition: max-height 200ms ease-out, border 200ms step-start, box-shadow 200ms linear;
  }
  .optionsVisible .field_selected_option{
    transition: none;
  }
  .option{
    padding: 15px;
    margin: 0;
    background-color: #FFFFFF;
    cursor: pointer;
    border-bottom: 1px solid var(--light-gray);
  }
  .option:last-child{
    border-bottom: none;
  }
  .option span{
    margin: 4px 0;
    display: inline-block;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
  .option:hover, .option.suggested{
    background-color: var(--extra-light-gray);
  }
  .option.selected{
    background-color: var(--light-blue);
    color: var(--blue);
    font-weight: 500;
  }
  .dropdown_icon{
    display: inline-block;
    margin-top: 1px;
    transform: rotateX(180deg);
    color: var(--gray);
    opacity: 0.5;
    transition: transform 180ms linear, opacity 180ms linear;
  }
  .optionsVisible .dropdown_icon{
    transform: rotateX(0deg);
    color: var(--blue);
    opacity: 1;
  }
  .selected_title, .selected_subtitle{
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .optionsVisible .field_selected_option{
    border-color: var(--blue);
    background-color: #FFFFFF;
  }

  .selected_text_wrapper{
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    justify-content: center;
  }
  .image_wrapper{
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .option_image{
    height: 100%;
  }
</style>

<div class="field {optionsVisible ? "optionsVisible" : ""} {!enabled ? "disabled" : ""} {showError ? "show_error" : ""}" style={fit_content ? "width: "+fit_content_width+"px" : ""}>
  <div class="field_selected_option" id={generatedID+"-selected"} on:click={selectedOptionClicked} use:popperRef>
    {#if optionForValue(value)?.image}
      <div class="image_wrapper">
        <img class="option_image" src={optionForValue(value).image}>
      </div>
    {/if}
    <div class="selected_text_wrapper">
      <span class="selected_title {"selected_title-"+generatedID}">{@html selected != "_default" ? $_(selected) : defaultSelected(options)}</span>
    </div>
    <i class="dropdown_icon fa-regular fa-chevron-up"></i>
  </div>
  <div id={generatedID} class="options" use:popperContent={popperOpts} style="width: {selected_width}px">
    {#each options as option, i}
      <div class="option {suggestedSelectionIndex == i ? "suggested" : ""} {option.selected ? "selected" : ""}" on:click={() => optionSelected(option.id, i)}>
        <span class="option_title">{@html $_(option.title)}</span>
        {#if option.subtitle}
          <span class="option_subtitle">{@html $_(option.subtitle)}</span>
        {/if}
      </div>
    {/each}
  </div>
</div>
